import gql from "graphql-tag";

import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {PermissionsInfo} from "@khan/shared-core";

import type {PlaybackRate} from "../types.ts";

type MuteVideosData = {
    setSettings:
        | {
              user: {
                  id: string;
                  muteVideos: boolean;
              };
          }
        | null
        | undefined;
};

type MuteVideosVariables = {
    muteVideos: boolean;
};

const MUTE_VIDEOS_MUTATION: GqlOperation<MuteVideosData, MuteVideosVariables> =
    gqlOp(gql`
        mutation updateMuteVideosSetting($muteVideos: Boolean!) {
            setSettings(muteVideos: $muteVideos) {
                user {
                    id
                    muteVideos
                }
            }
        }
    `);

type ShowCaptionsData = {
    setSettings:
        | {
              user: {
                  id: string;
                  showCaptions: boolean;
              };
          }
        | null
        | undefined;
};

type ShowCaptionsVariables = {
    showCaptions: boolean;
};

const SHOW_CAPTIONS_MUTATION: GqlOperation<
    ShowCaptionsData,
    ShowCaptionsVariables
> = gqlOp(gql`
    mutation updateShowCaptionsSetting($showCaptions: Boolean!) {
        setSettings(showCaptions: $showCaptions) {
            user {
                id
                showCaptions
            }
        }
    }
`);

type SetPlaybackRateData = {
    setSettings:
        | {
              user: {
                  id: string;
                  playbackRate: PlaybackRate;
              };
          }
        | null
        | undefined;
};

type SetPlaybackRateVariables = {
    playbackRate: PlaybackRate;
};

const SET_PLAYBACK_RATE_MUTATION: GqlOperation<
    SetPlaybackRateData,
    SetPlaybackRateVariables
> = gqlOp(gql`
    mutation updatePlaybackRateSetting($playbackRate: PlaybackRate!) {
        setSettings(playbackRate: $playbackRate) {
            user {
                id
                playbackRate
            }
        }
    }
`);

export const updateMuteVideos: (
    gqlClient: ApolloGqlClient,
    muteVideos: boolean,
) => Promise<unknown> | null | undefined = (gqlClient, muteVideos) => {
    // NOTE(jvoll):
    // It is possible that this mutation fails because we are trying to
    // set this setting before a user has been created. This would
    // happen if the user is not logged in, not yet a phantom, and
    // lands on a modal video using the fallback player. The
    // only user-facing consequence is that if that phantom user ends up
    // creating an account their mute setting might not be carried forward.
    // We originally decided this was acceptable and let this fail, however
    // this caused a huge number of errors and prevented us from writing
    // smoke tests for the fallback player.
    //
    // Therefore we are changing course and will only fire this
    // mutation if the user is known to be logged in or a phantom
    // (`hasProfile`). The drawback to this is that since phantoms are
    // created during video playback, and PermissionsInfo is not updated
    // until we do a full pageload, this will not set the mute setting for
    // newly created phantom users the first time they watch a video.
    // However, when the user returns a second time, we will have a phantom
    // for them and save their mute setting for subsequent visits.
    //
    // Once the `setSettings` mutation adopts ADR #303 and returns detailed
    // error codes, we could always fire this mutation and have a catch
    // block that looks for the specific error code and just swallows those
    // errors. Adding catch + swallow logic now would catch (and silence)
    // all errors from this mutation, which is undesireable.
    //
    // Context: https://khanacademy.slack.com/archives/C3Z5XRM7U/p1583947191040200
    if (PermissionsInfo.hasProfile) {
        return gqlClient.mutate(MUTE_VIDEOS_MUTATION, {
            variables: {
                muteVideos,
            },
        });
    }
};

export const updateShowCaptions: (
    gqlClient: ApolloGqlClient,
    showCaptions: boolean,
) => Promise<boolean | null | undefined> | null | undefined = (
    gqlClient,
    showCaptions,
) => {
    // see the updateMuteVideosSetting for some notes about this approach to using profile
    if (PermissionsInfo.hasProfile) {
        return gqlClient
            .mutate(SHOW_CAPTIONS_MUTATION, {
                variables: {
                    showCaptions,
                },
            })
            .then((result) => {
                return result.data?.setSettings?.user.showCaptions;
            });
    }
};

export const updatePlaybackRate: (
    gqlClient: ApolloGqlClient,
    playbackRate: PlaybackRate,
) => Promise<PlaybackRate | null | undefined> | null | undefined = (
    gqlClient,
    playbackRate,
) => {
    // see the updateMuteVideosSetting for some notes about this approach to using profile
    if (PermissionsInfo.hasProfile) {
        return gqlClient
            .mutate(SET_PLAYBACK_RATE_MUTATION, {
                variables: {
                    playbackRate,
                },
            })
            .then((result) => {
                return result.data?.setSettings?.user.playbackRate;
            });
    }
};
